// Comparison-video export job (F-13.1–F-13.6, S-07): decodes full-resolution frames in its own workers (never
// proxies, never drops), composites a template, converts to I420 BT.709 limited and encodes H.264/HEVC into MP4.

import {
  ALL_FORMATS,
  AudioBufferSink,
  AudioBufferSource,
  BlobSource,
  BufferTarget,
  Input,
  Mp4OutputFormat,
  Output,
  StreamTarget,
  VideoSample,
  VideoSampleSink,
  VideoSampleSource,
  canEncodeAudio,
  canEncodeVideo,
} from 'mediabunny';
import type { App } from '../app/app';
import { Feeder } from '../media/feeder';
import type { SourceId, SpatialTransform } from '../core/types';
import { frameLabel, heldAFrame } from '../core/timeline';
import { browserAssumedSetup, correctionMatrix, isIdentityAdjustment } from '../core/color';
import { displayTimecode } from '../core/timecode';
import { Compositor, drawClientBurnIn, drawInternalBurnIn, drawIntroFrame, drawSlate } from './compositor';
import { ZOOM_GRID_LABEL_FROM } from '../app/view';
import { layoutTemplate, type ClientTemplate, type InternalTemplate, type TemplateLayout } from './templates';
import { YuvPacker } from './yuv-pack';
import { alignSummary, sourceLabel } from '../ui/badges';
import { t } from '../i18n';

export interface ExportOptions {
  kind: 'internal' | 'client';
  template: InternalTemplate | ClientTemplate;
  range: 'all' | 'io';
  maxRate: boolean;
  half: boolean;
  codec: 'avc' | 'hevc';
  bitrateMbps: number;
  audio: boolean;
  /** Language of burned-in text (F-14: may differ from the interface language). */
  lang?: 'zh-TW' | 'en';
  internal: { burnTc: boolean; burnAlign: boolean; applyAdjust: boolean; burnCross: boolean; burnGrid: boolean };
  /**
   * Opening (F-13.8): a text card (slate, 2 s at the start and at the end) and/or a chosen video at the start,
   * letterboxed on black and silent; introSec is its duration for the estimate.
   */
  client: { before: string; after: string; slate: boolean; slateTitle: string; slateSubtitle: string; intro?: File | null; introSec?: number };
}

export interface ExportPlan {
  layout: TemplateLayout;
  sources: SourceId[];
  ticks: number[];
  fps: number;
  durationSec: number;
  estimatedBytes: number;
  scaledNote: boolean;
}

export function planExport(app: App, o: ExportOptions): ExportPlan {
  const a = app.sources.source('A');
  const k = app.project.compareScale;
  const gw = (a?.spec.width ?? 1920) * k;
  const gh = (a?.spec.height ?? 1080) * k;
  const bs = app.sources.loadedBs();
  const selB = app.project.layout.selectedB;
  const orderedBs = [selB, ...bs.filter((b) => b !== selB)].filter((b) => app.sources.has(b));
  const layout = layoutTemplate(o.template, gw, gh, orderedBs, o.half, orderedBs[0] ? app.view.compareBaseFor(orderedBs[0]) : 'A');
  const needed = new Set<SourceId>();
  for (const c of layout.cells) {
    if (app.sources.has(c.src)) needed.add(c.src);
    if (c.b && app.sources.has(c.b)) needed.add(c.b);
  }
  const tl = app.sources.timeline;
  const { lo, hi } = o.range === 'io' ? app.player.bounds() : { lo: 0, hi: tl.totalTicks - 1 };
  const step = o.maxRate ? 1 : tl.ticksPerFrame;
  const ticks: number[] = [];
  // Start on the A frame that contains the in point, even if the in point sits on a generated tick.
  for (let tk = lo - (lo % step); tk <= hi; tk += step) ticks.push(tk);
  const fps = tl.aFrameRate * (tl.ticksPerFrame / step);
  const durationSec = ticks.length / fps + (o.kind === 'client' && o.client.slate ? 4 : 0) + (o.kind === 'client' && o.client.intro ? (o.client.introSec ?? 0) : 0);
  const estimatedBytes = (o.bitrateMbps * 1e6 * durationSec) / 8 + (o.audio ? (192000 * durationSec) / 8 : 0);
  return { layout, sources: [...needed], ticks, fps, durationSec, estimatedBytes, scaledNote: layout.scale < 1 };
}


export class ExportJob {
  progress = 0;
  cancelled = false;
  error: string | null = null;
  onProgress: ((p: number, frame: number, total: number) => void) | null = null;

  constructor(
    private app: App,
    private o: ExportOptions,
  ) {}

  cancel(): void {
    this.cancelled = true;
  }

  /** Runs the export. target: a writable file stream (File System Access) or null for an in-memory Blob. */
  async run(writable: FileSystemWritableFileStream | null): Promise<Blob | null> {
    const app = this.app;
    const o = this.o;
    const plan = planExport(app, o);
    const L = plan.layout;
    const tl = app.sources.timeline;
    const tickSec = 1 / (tl.aFrameRate * tl.ticksPerFrame);
    const startSec = plan.ticks.length ? plan.ticks[0] * tickSec : 0;
    const endSec = plan.ticks.length ? (plan.ticks[plan.ticks.length - 1] + (o.maxRate ? 1 : tl.ticksPerFrame)) * tickSec : 0;
    const slateSec = o.kind === 'client' && o.client.slate ? 2 : 0;
    let output: Output | null = null;
    let target: StreamTarget | BufferTarget;
    let comp: Compositor | null = null;
    let packer: YuvPacker | null = null;
    let audioInput: Input | null = null;
    const feeders = new Map<SourceId, Feeder>();
    // On cancel or failure the partial file must not be left behind as if it were complete.
    const abort = async () => {
      try {
        if (writable) await writable.abort();
      } catch {
        /* already closed */
      }
      try {
        await output?.cancel();
      } catch {
        /* ignore */
      }
    };
    try {
      if (!(await canEncodeVideo(o.codec, { width: L.width, height: L.height }))) throw new Error(t('export.errCodec', { codec: o.codec, w: L.width, h: L.height }));
      target = writable ? new StreamTarget(writable as unknown as WritableStream, { chunked: true }) : new BufferTarget();
      output = new Output({ format: new Mp4OutputFormat({ fastStart: writable ? false : 'in-memory' }), target });
      const video = new VideoSampleSource({ codec: o.codec, bitrate: o.bitrateMbps * 1e6, keyFrameInterval: 1, latencyMode: 'quality' });
      output.addVideoTrack(video, { frameRate: plan.fps });

      // Audio from the selected source (F-13.4); muted or unavailable → no audio track.
      let audioSrc: AudioBufferSource | null = null;
      let audioIter: AsyncGenerator<{ buffer: AudioBuffer; timestamp: number }, void, unknown> | null = null;
      let audioT0 = 0;
      let audioRate = 48000;
      let audioCh = 2;
      const audioId = app.project.audioSourceId;
      if (o.audio && audioId && app.sources.runtime.get(audioId)?.info.hasAudio) {
        const aCodec = (await canEncodeAudio('aac')) ? 'aac' : (await canEncodeAudio('opus')) ? 'opus' : null;
        if (aCodec) {
          audioInput = new Input({ formats: ALL_FORMATS, source: new BlobSource(app.sources.runtime.get(audioId)!.client.media as File) });
          const tr = await audioInput.getPrimaryAudioTrack();
          if (tr) {
            audioRate = await tr.getSampleRate();
            audioCh = await tr.getNumberOfChannels();
            audioSrc = new AudioBufferSource({ codec: aCodec, bitrate: 192000 });
            output.addAudioTrack(audioSrc);
            const src = app.sources.source(audioId)!;
            const offset = audioId === 'A' ? 0 : src.frameOffset / src.spec.frameRate;
            const first = app.sources.runtime.get(audioId)!.info.firstTimestamp;
            audioT0 = first + startSec + offset;
            audioIter = new AudioBufferSink(tr).buffers(Math.max(0, audioT0), first + endSec + offset) as AsyncGenerator<
              { buffer: AudioBuffer; timestamp: number },
              void,
              unknown
            >;
          }
        }
      }
      await output.start();
      comp = new Compositor(L);
      packer = new YuvPacker(L.width, L.height);
      for (const id of plan.sources) {
        const f = new Feeder(app.sources.runtime.get(id)!.client.media);
        feeders.set(id, f);
        await f.open();
      }
      const dur = 1 / plan.fps;
      let ts = 0;
      // Audio is written contiguously: gaps (audio starting late, holes, negative offsets) are filled with silence
      // so it never drifts ahead of the picture.
      let audioWritten = 0; // seconds of programme audio written (excluding title cards)
      const silence = async (sec: number) => {
        if (!audioSrc || sec <= 1e-4) return;
        await audioSrc.add(new AudioBuffer({ length: Math.max(1, Math.round(sec * audioRate)), numberOfChannels: audioCh, sampleRate: audioRate }));
      };
      const pumpAudio = async (untilSec: number) => {
        if (!audioSrc) return;
        while (audioWritten < untilSec) {
          const next = audioIter ? (await audioIter.next()).value : undefined;
          if (!next) {
            audioIter = null;
            // Pad with silence to the end of the picture.
            const until = Math.min(untilSec, endSec - startSec);
            if (until > audioWritten) {
              await silence(until - audioWritten);
              audioWritten = until;
            }
            return;
          }
          let buf = next.buffer;
          const rel = next.timestamp - audioT0;
          if (rel > audioWritten + 1 / audioRate) {
            await silence(rel - audioWritten);
            audioWritten = rel;
          }
          const skip = Math.round((audioWritten - rel) * buf.sampleRate);
          if (skip >= buf.length) continue;
          if (skip > 0) buf = sliceBuffer(buf, skip, buf.length);
          await audioSrc.add(buf);
          audioWritten = rel + next.buffer.duration;
        }
      };
      const slate = async () => {
        await silence(slateSec);
        for (let i = 0; i < Math.round(slateSec * plan.fps); i++) {
          drawSlate(comp!, o.client.slateTitle, o.client.slateSubtitle);
          await this.encode(video, packer!, comp!, ts, dur);
          ts += dur;
        }
      };
      // Opening video (F-13.8): its frames at the export frame rate, without its sound.
      const intro = async () => {
        const file = o.kind === 'client' ? o.client.intro : null;
        if (!file) return;
        const input = new Input({ formats: ALL_FORMATS, source: new BlobSource(file) });
        try {
          const tr = await input.getPrimaryVideoTrack();
          if (!tr) throw new Error(t('export.errIntro', { name: file.name }));
          const t0 = await tr.getFirstTimestamp();
          const n = Math.max(1, Math.round((await tr.computeDuration()) * plan.fps));
          await silence(n * dur);
          const times = Array.from({ length: n }, (_, i) => t0 + i / plan.fps);
          for await (const sample of new VideoSampleSink(tr).samplesAtTimestamps(times)) {
            if (this.cancelled) break;
            drawIntroFrame(comp!, sample);
            sample?.close();
            await this.encode(video, packer!, comp!, ts, dur);
            ts += dur;
          }
        } finally {
          input.dispose();
        }
      };
      if (slateSec) await slate();
      await intro();
      const total = plan.ticks.length;
      for (let i = 0; i < total; i++) {
        if (this.cancelled) break;
        const tick = plan.ticks[i];
        await this.uploadFrames(comp, feeders, tick);
        if (this.cancelled) break;
        this.drawFrame(comp, tick, i / Math.max(1, total - 1));
        await pumpAudio((i + 1) * dur + 1);
        await this.encode(video, packer, comp, ts, dur);
        ts += dur;
        this.progress = (i + 1) / total;
        this.onProgress?.(this.progress, i + 1, total);
      }
      if (this.cancelled) {
        await abort();
        return null;
      }
      await pumpAudio(endSec - startSec);
      if (slateSec) await slate();
      await output.finalize();
      if (target instanceof BufferTarget && target.buffer) return new Blob([target.buffer], { type: 'video/mp4' });
      return null;
    } catch (err) {
      this.error = String(err);
      await abort();
      if (this.cancelled) return null;
      throw err;
    } finally {
      for (const f of feeders.values()) f.dispose();
      packer?.dispose();
      comp?.gl.dispose();
      audioInput?.dispose();
    }
  }

  private async uploadFrames(comp: Compositor, feeders: Map<SourceId, Feeder>, tick: number): Promise<void> {
    const app = this.app;
    const step = this.o.maxRate ? 1 : app.sources.timeline.ticksPerFrame;
    for (const [id, feeder] of feeders) {
      const at = app.sources.frameAt(id, tick);
      const f = at?.frame;
      if (f === null || f === undefined) {
        comp.gl.clearSource(id);
        continue;
      }
      const ahead: number[] = [];
      for (let j = 1; j <= 8; j++) {
        const nf = app.sources.frameAt(id, tick + j * step)?.frame;
        if (nf !== null && nf !== undefined && !ahead.includes(nf)) ahead.push(nf);
      }
      const frame = await feeder.get(f, ahead, () => this.cancelled);
      const color = app.sources.color(id);
      if (frame instanceof VideoFrame) comp.gl.uploadVideoFrame(id, frame, correctionMatrix(browserAssumedSetup(frame.colorSpace), color), `${f}`);
      else comp.gl.uploadFrame(id, frame, color, `${f}`);
      feeder.release(f, f + 64);
    }
  }

  private drawFrame(comp: Compositor, tick: number, progress01: number): void {
    const app = this.app;
    const o = this.o;
    const p = app.project;
    const fa = { w: app.sources.source('A')?.spec.width ?? 1920, h: app.sources.source('A')?.spec.height ?? 1080 };
    const transforms: Partial<Record<SourceId, SpatialTransform>> = {};
    const native: Partial<Record<SourceId, { w: number; h: number }>> = {};
    for (const id of app.sources.loadedIds()) {
      const s = app.sources.source(id)!;
      transforms[id] = s.spatialTransform;
      native[id] = { w: s.spec.width, h: s.spec.height };
    }
    const internal = o.kind === 'internal';
    const adj = internal && o.internal.applyAdjust && p.viewAdjustment.enabled && !isIdentityAdjustment(p.viewAdjustment) ? p.viewAdjustment : null;
    const aFrame = heldAFrame(app.sources.timeline, tick);
    // Client dynamic wipe: the split sweeps back and forth.
    const wipePos = !internal && o.template === 'wipe' ? 0.5 + 0.4 * Math.sin(progress01 * Math.PI * 2) : p.layout.wipe.position;
    comp.drawVideo(
      { frameA: fa, transforms, native },
      {
        crosshair: p.layout.crosshair.position,
        zoom: p.layout.crosshair.zoomPercent / 100,
        nearest: p.layout.view.sampling === 'nearest',
        diff: { gain: p.layout.difference.gain, heat: p.layout.difference.display === 'heat', luma: p.layout.difference.lumaOnly, band: ({ all: 0, low: 1, high: 2 } as const)[p.layout.difference.band ?? 'all'] },
        wipe: { pos: wipePos, angleRad: internal ? (p.layout.wipe.angleDeg * Math.PI) / 180 : 0 },
        adjust: adj,
      },
    );
    if (internal) {
      const notes: string[] = [];
      if (comp.layout.scale < 1) notes.push(t('export.burnScaled', { p: Math.round(comp.layout.scale * 100) }, o.lang));
      if (adj) notes.push(t('export.burnAdjusted', undefined, o.lang));
      const labels: Partial<Record<SourceId, string>> = {};
      const alignInfo: Partial<Record<SourceId, string>> = {};
      for (const id of app.sources.loadedIds()) {
        labels[id] = sourceLabel(app, id);
        if (o.internal.burnAlign && id !== 'A') alignInfo[id] = alignSummary(app, id, o.lang);
      }
      const a = app.sources.source('A');
      drawInternalBurnIn(comp, {
        labels,
        alignInfo,
        timecode: o.internal.burnTc ? displayTimecode(aFrame, app.sources.timeline.aFrameRate, p.timecodeMode, a?.spec.startTimecode) : null,
        frameLabel: o.internal.burnTc ? 'F ' + frameLabel(app.sources.timeline, tick, app.state.settings.frameNumberBase) : null,
        crosshair: o.internal.burnCross ? p.layout.crosshair.position : null,
        center: p.layout.crosshair.position,
        zoom: p.layout.crosshair.zoomPercent / 100,
        grid: o.internal.burnGrid ? { labels: p.layout.crosshair.zoomPercent >= ZOOM_GRID_LABEL_FROM } : null,
        notes,
        frameA: fa,
      });
    } else {
      drawClientBurnIn(comp, { labels: { before: o.client.before, after: o.client.after }, present: app.sources.loadedIds() });
    }
  }

  private async encode(video: VideoSampleSource, packer: YuvPacker, comp: Compositor, ts: number, dur: number): Promise<void> {
    const bytes = packer.pack(comp.canvas);
    const sample = new VideoSample(bytes, {
      format: 'I420',
      codedWidth: comp.layout.width,
      codedHeight: comp.layout.height,
      timestamp: ts,
      duration: dur,
      colorSpace: { primaries: 'bt709', transfer: 'bt709', matrix: 'bt709', fullRange: false },
    });
    try {
      await video.add(sample);
    } finally {
      sample.close();
    }
  }
}

function sliceBuffer(b: AudioBuffer, from: number, to: number): AudioBuffer {
  const len = Math.max(0, Math.min(b.length, to) - Math.max(0, from));
  const out = new AudioBuffer({ length: Math.max(1, len), numberOfChannels: b.numberOfChannels, sampleRate: b.sampleRate });
  if (!len) return new AudioBuffer({ length: 1, numberOfChannels: b.numberOfChannels, sampleRate: b.sampleRate });
  for (let c = 0; c < b.numberOfChannels; c++) out.copyToChannel(b.getChannelData(c).subarray(from, from + len), c);
  return out;
}
